// Pure helpers behind the administration screens (design system 06-modules §1–§3). DOM-free so
// they are unit-tested under node: rights attribution, the roster's filter model and its URL
// round-trip, the grants editor's act ⇒ view clamp, the blast-radius wording, and the audit log's
// filters, operation labels and deep-link resolution. Pages compose these; they do not decide
// access — the server re-resolves every operation (permission-model P8/P9).
import type {
  AdminGroupRow,
  AdminRight,
  AdminUserRow,
  AuditEventDto,
  EffectiveAccessPreview,
  GroupBlastRadius,
  GroupGrants,
  GroupSectionGrant,
  GroupWarning,
} from '../../shared/api-types.js';
import {
  ADMIN_SECTION_DESCRIPTIONS,
  ADMIN_SECTION_LABELS,
  ADMIN_SECTION_REACH,
  ADMIN_SECTIONS,
  AUDIT_OUTCOME_LABELS,
  AUDIT_OUTCOMES,
  AUDIT_SURFACE_LABELS,
  AUDIT_SURFACES,
  CAPABILITY_REQUIRES,
  GLOBAL_CAPABILITIES,
  GLOBAL_CAPABILITY_DESCRIPTIONS,
  GLOBAL_CAPABILITY_LABELS,
  SUBJECT_STATE_WORDS,
  SUBJECT_STATUS_LABELS,
  SUBJECT_STATUSES,
  isOneOf,
  type AdminSection,
  type AuditOutcome,
  type GlobalCapability,
  type SubjectStatus,
} from '../../shared/enums.js';
import { deletedAccountLabel } from '../../shared/text.js';
import { reasonInWords } from '../pages/lib/activity.js';
import { formatCount, formatList, pluralise } from '../ui/format.js';
import type { Tone } from '../ui/StatusPill.js';
import type { SortValue } from '../ui/sort-params.js';

// --- Account state ------------------------------------------------------------------------------

/**
 * The state word on the pill is the shared one (enums.ts SUBJECT_STATE_WORDS, per 05-content §6);
 * the precise lifecycle label (SUBJECT_STATUS_LABELS) is shown beside the pill on the account page.
 */
export const STATE_WORDS: Readonly<Record<SubjectStatus, string>> = SUBJECT_STATE_WORDS;

export const STATE_TONES: Readonly<Record<SubjectStatus, Tone>> = {
  approved: 'success',
  pending: 'warning',
  suspended: 'neutral',
  denied: 'danger',
};

/** Sort rank for the state column: live accounts first, then dormant, then refused, then waiting. */
export const STATE_RANK: Readonly<Record<SubjectStatus, number>> = {
  approved: 0,
  suspended: 1,
  denied: 2,
  pending: 3,
};

/**
 * What an account's deletion takes, said in its confirmation before it is pressed (03-components §4.2: "Body states what
 * is lost and whether it can be undone — including what other people lose by the act"). Why four sentences and not the
 * one it was (the Calis conformity re-audit of 2026-10-10, R22, and the standard's DS-26): "its meetings, summaries and
 * to-dos" omitted the projects, the Fellow and Slack connections and what was read from Slack, all of which go with the
 * account (the schema cascades them), and said nothing of what colleagues lose — the projects it shared with them, the
 * shares they offered it, its side of their linked to-dos. And the owner's ruling of 2026-10-10 on erasure, "Leave and
 * disclose": colleagues' own records keep the person's name as it was written there, and the confirmation says so, as
 * SECURITY.md does. Shared by the roster's dialog and the account page's, so the two never say different things.
 * Why the records line names transcripts and summaries and says "for example" (the review of the Phase 2c landing,
 * 2026-10-10): a colleague's own copy of a meeting the person attended keeps its transcript — each speaker's name and
 * words — and its summary, which names people, and nothing cascades or scrubs either (they go only with their owner);
 * the line listed three kinds after a colon, read as complete, and left out where the name and the words most often stay.
 * Other content of a colleague's — a to-do's own text, a note — can name the person too, so the list is examples.
 */
export const ACCOUNT_DELETION_WORDS = Object.freeze({
  theirs: 'This permanently removes the account and everything it holds: its meetings, summaries, to-dos, notes and briefs, its projects, its Fellow and Slack connections, and everything Calis read from Slack for it.',
  colleagues: 'Colleagues lose what it shared and what was shared with it: every project it shared stops reaching the colleagues it was shared with, every project shared with it stops being shared, and its linked to-dos are unlinked — each colleague keeps their own copy as it stands.',
  records: 'Colleagues’ own records keep this person’s name, and their words, as they were written there — for example a meeting’s transcript, summary and attendees, a to-do’s Responsible, a Slack line quoted in a check.',
  closing: 'Audit records remain. It cannot be undone.',
});

export function stateTitle(status: SubjectStatus): string {
  return SUBJECT_STATUS_LABELS[status];
}

// --- Rights attribution (06-modules §1.5) -------------------------------------------------------

export type RightChipKind = AdminRight['kind'];

export interface RightChip {
  key: string;
  kind: RightChipKind;
  label: string;
  /** The conferring group, when the chip is one. */
  groupId?: string;
  /** The administrative section, when the chip is a section grant. */
  section?: AdminSection;
  /** What a section grant opens, said in words: "view", or "view and act" where the server reports act. */
  verbs?: string;
  capability?: GlobalCapability;
}

/** The superuser pseudo-capability value in the capability facet: it is a fact worth filtering by. */
export const SUPERUSER_FACET = 'superuser';
export const SUPERUSER_LABEL = 'Superuser';

/**
 * A section grant's chip text: the section and its reach, always both (03-groups §3.6: an instance-wide grant must
 * never read as a local one), e.g. "Users · instance-wide".
 */
export function sectionRightLabel(section: AdminSection): string {
  return `${ADMIN_SECTION_LABELS[section]} · ${sectionReachLabel(section)}`;
}

/**
 * One chip per conferring source, in a fixed order: the superuser marker first (it sits outside
 * the grant engine and no group removal touches it), then each group, then each administrative
 * section those groups grant (06-modules §1.5 rule 2: its own chip, D8), then each capability.
 * Returns null when the rights could not be read — a different fact from "none".
 */
export function describeRights(rights: unknown): RightChip[] | null {
  if (!Array.isArray(rights)) return null;
  const chips: RightChip[] = [];
  const seen = new Set<string>();
  const push = (chip: RightChip): void => {
    if (seen.has(chip.key)) return;
    seen.add(chip.key);
    chips.push(chip);
  };
  for (const right of rights as AdminRight[]) {
    if (right.kind === 'superuser') push({ key: 'superuser', kind: 'superuser', label: SUPERUSER_LABEL });
  }
  for (const right of rights as AdminRight[]) {
    if (right.kind === 'group') push({ key: `group:${right.id}`, kind: 'group', label: right.name, groupId: right.id });
  }
  for (const right of rights as AdminRight[]) {
    if (right.kind === 'section' && isOneOf(ADMIN_SECTIONS, right.section)) {
      // Why: the server reports `act` only where a delegable route honours it (pick C4); anything else reads "view".
      const verbs = Array.isArray(right.actions) && right.actions.includes('act') ? 'view and act' : 'view';
      push({ key: `section:${right.section}`, kind: 'section', label: sectionRightLabel(right.section), section: right.section, verbs });
    }
  }
  for (const right of rights as AdminRight[]) {
    if (right.kind === 'capability' && isOneOf(GLOBAL_CAPABILITIES, right.name)) {
      push({ key: `capability:${right.name}`, kind: 'capability', label: GLOBAL_CAPABILITY_LABELS[right.name], capability: right.name });
    }
  }
  return chips;
}

/** Text the rights column sorts by: the chip labels in display order; no rights sorts last. */
export function rightsSortText(rights: AdminRight[]): string | null {
  const chips = describeRights(rights) ?? [];
  if (chips.length === 0) return null;
  return chips.map((chip) => chip.label).join(', ');
}

export interface SectionHeld {
  section: AdminSection;
  /** The section's name, e.g. "Users". */
  name: string;
  /** What the grant opens and how far it reaches, as a sentence: "View, instance-wide." */
  reading: string;
  /** What holding the section shows (enums.ts ADMIN_SECTION_DESCRIPTIONS, the words the group editor gives). */
  description: string;
}

/**
 * The administrative sections an account holds, in full words, for the account page's read-only list — shown to every
 * reader of the account, a users:view delegate included (owner ruling O1 of 2026-10-05), where before they appeared
 * only inside the superuser's group checkboxes (D8). Drawn from the same chips the row shows, so the two never differ.
 */
export function sectionsHeld(rights: unknown): SectionHeld[] {
  const held: SectionHeld[] = [];
  for (const chip of describeRights(rights) ?? []) {
    if (chip.kind !== 'section' || chip.section === undefined) continue;
    const verbs = chip.verbs ?? 'view';
    held.push({
      section: chip.section,
      name: ADMIN_SECTION_LABELS[chip.section],
      reading: `${verbs.charAt(0).toUpperCase()}${verbs.slice(1)}, ${sectionReachLabel(chip.section)}.`,
      description: ADMIN_SECTION_DESCRIPTIONS[chip.section],
    });
  }
  return held;
}

// --- Roster filters (06-modules §1.3 R3; 04-data §1.5) ------------------------------------------

export interface RosterFilters {
  /** Free text across display name and email. */
  q: string;
  /** A conferring group id. */
  group: string;
  /** An administrative section held (06-modules §1.3 R3: the section facet, A15). */
  section: string;
  /** A global capability name, or `superuser`. */
  capability: string;
  /** An admission status. */
  state: string;
}

export const EMPTY_ROSTER_FILTERS: Readonly<RosterFilters> = Object.freeze({ q: '', group: '', section: '', capability: '', state: '' });

const ROSTER_PARAM_KEYS = ['q', 'group', 'section', 'capability', 'state'] as const;

export function parseRosterFilters(params: URLSearchParams): RosterFilters {
  return {
    q: (params.get('q') ?? '').trim(),
    group: (params.get('group') ?? '').trim(),
    section: (params.get('section') ?? '').trim(),
    capability: (params.get('capability') ?? '').trim(),
    state: (params.get('state') ?? '').trim(),
  };
}

/** Writes the filters into a copy of the params; empty values are removed so a pristine URL stays clean. */
export function writeRosterFilters(params: URLSearchParams, filters: RosterFilters): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of ROSTER_PARAM_KEYS) {
    const value = filters[key].trim();
    if (value === '') next.delete(key);
    else next.set(key, value);
  }
  return next;
}

/** Structured filters only — the search is its own control and stays visible outside the sheet. */
export function activeRosterFilterCount(filters: RosterFilters): number {
  return [filters.group, filters.section, filters.capability, filters.state].filter((value) => value.trim() !== '').length;
}

export function rosterFiltersActive(filters: RosterFilters): boolean {
  return filters.q.trim() !== '' || activeRosterFilterCount(filters) > 0;
}

function searchTerms(query: string): string[] {
  return query.toLowerCase().split(/\s+/u).filter((term) => term !== '');
}

/** Every search term must appear in the display name or the email (both lower-cased). */
export function matchesSearch(row: Pick<AdminUserRow, 'displayName' | 'email'>, query: string): boolean {
  const terms = searchTerms(query);
  if (terms.length === 0) return true;
  const haystack = `${row.displayName}\n${row.email}`.toLowerCase();
  return terms.every((term) => haystack.includes(term));
}

/**
 * A row's rights as a list, or none where they could not be read. Why (06-modules §1.8 Partial; ruling O9's pin found it):
 * one row whose rights are not a list must mark its own cell "Could not load" (`describeRights` answers null for it, and
 * `RightsChips` says so) while the table stands — so the filter bar's facets and its matcher, which run over every row,
 * read such a row as holding nothing rather than iterate the value and take the whole page down with them.
 */
function readableRights(row: Pick<AdminUserRow, 'rights'>): readonly AdminRight[] {
  return Array.isArray(row.rights) ? row.rights : [];
}

export function matchesRoster(row: AdminUserRow, filters: RosterFilters): boolean {
  if (!matchesSearch(row, filters.q)) return false;
  const rights = readableRights(row);
  if (filters.group !== '' && !rights.some((right) => right.kind === 'group' && right.id === filters.group)) return false;
  if (filters.section !== '' && !rights.some((right) => right.kind === 'section' && right.section === filters.section)) return false;
  if (filters.capability !== '') {
    const holds = filters.capability === SUPERUSER_FACET
      ? rights.some((right) => right.kind === 'superuser')
      : rights.some((right) => right.kind === 'capability' && right.name === filters.capability);
    if (!holds) return false;
  }
  if (filters.state !== '' && row.status !== filters.state) return false;
  return true;
}

export function filterRoster(rows: readonly AdminUserRow[], filters: RosterFilters): AdminUserRow[] {
  return rows.filter((row) => matchesRoster(row, filters));
}

export interface FacetOption {
  value: string;
  label: string;
}

export interface RosterFacets {
  groups: FacetOption[];
  sections: FacetOption[];
  capabilities: FacetOption[];
  states: FacetOption[];
}

/**
 * The two count headings of the users module (06-modules §1.3 R2; §1.8; 04-data §1.5; Phase 1c finding D22). A count is
 * stated only when it is known: the users have loaded (`ready`) and — for the roster — hold an account, since an empty
 * roster while the viewer is themselves an account is a failure, shown as one (§1.8). A failed or refused load states no
 * number at all, so the reader is never told "0 pending" or "Accounts (0)" above the failure that says why there is none.
 */
export function pendingCountKnown(status: 'loading' | 'ready' | 'error'): boolean {
  return status === 'ready';
}

/** The roster's heading: its total, "N of M" while filtering, or the bare noun while the total is not known. */
export function rosterHeadingText(status: 'loading' | 'ready' | 'error', total: number, matching: number, filtering: boolean): string {
  if (status !== 'ready' || total === 0) return 'Accounts';
  return filtering ? `Accounts — ${formatCount(matching)} of ${formatCount(total)}` : `Accounts (${formatCount(total)})`;
}

/**
 * Facet values drawn only from the loaded set (R3): a control can never select a value that
 * matches nothing. A value present in the URL but absent from the set is kept, labelled as such,
 * so the reader can see and remove it rather than wonder why the list is empty.
 */
export function rosterFacets(rows: readonly AdminUserRow[], selected: RosterFilters = EMPTY_ROSTER_FILTERS): RosterFacets {
  const groups = new Map<string, string>();
  const sections = new Map<string, string>();
  const capabilities = new Map<string, string>();
  const states = new Set<SubjectStatus>();
  for (const row of rows) {
    states.add(row.status);
    for (const right of readableRights(row)) {
      if (right.kind === 'group') groups.set(right.id, right.name);
      else if (right.kind === 'section' && isOneOf(ADMIN_SECTIONS, right.section)) sections.set(right.section, sectionRightLabel(right.section));
      else if (right.kind === 'capability' && isOneOf(GLOBAL_CAPABILITIES, right.name)) capabilities.set(right.name, GLOBAL_CAPABILITY_LABELS[right.name]);
      else if (right.kind === 'superuser') capabilities.set(SUPERUSER_FACET, SUPERUSER_LABEL);
    }
  }
  const byLabel = (a: FacetOption, b: FacetOption) => a.label.localeCompare(b.label, 'en', { sensitivity: 'base' });
  const groupOptions = [...groups].map(([value, label]) => ({ value, label })).sort(byLabel);
  const sectionOptions = [...sections].map(([value, label]) => ({ value, label })).sort(byLabel);
  const capabilityOptions = [...capabilities].map(([value, label]) => ({ value, label })).sort(byLabel);
  const stateOptions: FacetOption[] = SUBJECT_STATUSES.filter((status) => states.has(status)).map((status) => ({ value: status, label: STATE_WORDS[status] }));

  if (selected.group !== '' && !groups.has(selected.group)) groupOptions.push({ value: selected.group, label: `Unknown group (${selected.group})` });
  if (selected.section !== '' && !sections.has(selected.section)) {
    const known = isOneOf(ADMIN_SECTIONS, selected.section) ? sectionRightLabel(selected.section) : selected.section;
    sectionOptions.push({ value: selected.section, label: `${known} (none loaded)` });
  }
  if (selected.capability !== '' && !capabilities.has(selected.capability)) {
    const known = isOneOf(GLOBAL_CAPABILITIES, selected.capability) ? GLOBAL_CAPABILITY_LABELS[selected.capability] : selected.capability;
    capabilityOptions.push({ value: selected.capability, label: `${known} (none loaded)` });
  }
  if (selected.state !== '' && !stateOptions.some((option) => option.value === selected.state)) {
    const known = isOneOf(SUBJECT_STATUSES, selected.state) ? STATE_WORDS[selected.state] : selected.state;
    stateOptions.push({ value: selected.state, label: `${known} (none loaded)` });
  }
  return { groups: groupOptions, sections: sectionOptions, capabilities: capabilityOptions, states: stateOptions };
}

export interface FilterChip {
  key: keyof RosterFilters | string;
  label: string;
}

/** The active filters as chip text ("Group: Editors"), for the always-visible rail (03-components §10). */
export function describeRosterFilters(filters: RosterFilters, facets: RosterFacets): FilterChip[] {
  const chips: FilterChip[] = [];
  const labelOf = (options: FacetOption[], value: string): string => options.find((option) => option.value === value)?.label ?? value;
  if (filters.q.trim() !== '') chips.push({ key: 'q', label: `Search: ${filters.q.trim()}` });
  if (filters.group !== '') chips.push({ key: 'group', label: `Group: ${labelOf(facets.groups, filters.group)}` });
  if (filters.section !== '') chips.push({ key: 'section', label: `Section: ${labelOf(facets.sections, filters.section)}` });
  if (filters.capability !== '') chips.push({ key: 'capability', label: `Capability: ${labelOf(facets.capabilities, filters.capability)}` });
  if (filters.state !== '') chips.push({ key: 'state', label: `State: ${labelOf(facets.states, filters.state)}` });
  return chips;
}

export type RosterSortKey = 'name' | 'rights' | 'state' | 'last_seen' | 'decided';

/** The raw value each roster column sorts by; the table applies the type-aware, empties-last rule. */
export function rosterSortValue(row: AdminUserRow, key: RosterSortKey): SortValue {
  switch (key) {
    case 'name':
      return `${row.displayName} ${row.email}`;
    case 'rights':
      return rightsSortText(row.rights);
    case 'state':
      return STATE_RANK[row.status];
    case 'last_seen':
      return row.lastSeenAt;
    case 'decided':
      return row.decidedAt;
    default:
      return null;
  }
}

// --- Grants editor (permission-model 03-groups §3.6: act ⇒ view) --------------------------------

/** Every declared section present exactly once, in enum order, so the editor renders a fixed grid. */
export function normaliseGrants(grants: GroupGrants): GroupGrants {
  const sections: GroupSectionGrant[] = ADMIN_SECTIONS.map((section) => {
    const found = grants.sections.find((entry) => entry.section === section);
    const view = found?.view === true;
    return { section, view, act: view && found?.act === true };
  });
  const globals = GLOBAL_CAPABILITIES.filter((name) => grants.globals.includes(name));
  return { scopeMember: grants.scopeMember, sections, globals };
}

/** Ticking act ticks view; unticking view unticks act. Returns a new object; never mutates. */
export function setSectionGrant(grants: GroupGrants, section: AdminSection, change: { view?: boolean; act?: boolean }): GroupGrants {
  const base = normaliseGrants(grants);
  return {
    ...base,
    sections: base.sections.map((entry) => {
      if (entry.section !== section) return entry;
      let view = change.view ?? entry.view;
      let act = change.act ?? entry.act;
      if (change.act === true) view = true;
      if (change.view === false) act = false;
      return { section, view, act };
    }),
  };
}

export function setGlobalGrant(grants: GroupGrants, capability: GlobalCapability, granted: boolean): GroupGrants {
  const base = normaliseGrants(grants);
  const globals = GLOBAL_CAPABILITIES.filter((name) => (name === capability ? granted : base.globals.includes(name)));
  return { ...base, globals };
}

export function setScopeMember(grants: GroupGrants, scopeMember: boolean): GroupGrants {
  return { ...normaliseGrants(grants), scopeMember };
}

export function grantsEqual(a: GroupGrants, b: GroupGrants): boolean {
  const left = normaliseGrants(a);
  const right = normaliseGrants(b);
  if (left.scopeMember !== right.scopeMember) return false;
  if (left.globals.join(',') !== right.globals.join(',')) return false;
  return left.sections.every((entry, index) => {
    const other = right.sections[index];
    return other !== undefined && other.section === entry.section && other.view === entry.view && other.act === entry.act;
  });
}

// --- What a group confers, as chip text (06-modules §2.2) ---------------------------------------

export const SCOPE_MEMBER_LABEL = 'Member of Calis';

export function sectionReachLabel(section: AdminSection): string {
  return ADMIN_SECTION_REACH[section] === 'instance' ? 'instance-wide' : 'this workspace';
}

/**
 * One phrase per conferred grant: base access, each section with its reach stated (05-administration
 * §4: an instance-wide tick must never be mistaken for a local one), each capability. `actDelegable` is the server's
 * word on which sections any delegable route honours `act` for (`AdminGroupsResponse.actDelegableSections`, or the
 * detail's preview rows): a stored `act` is worded as conferred only there, since elsewhere it opens nothing — "what a
 * member will actually be able to do" (§4; owner's pick C4 of 2026-10-05; review finding 2). No list, no "and act".
 */
export function describeConfers(grants: GroupGrants, actDelegable: readonly AdminSection[]): string[] {
  const phrases: string[] = [];
  if (grants.scopeMember) phrases.push(SCOPE_MEMBER_LABEL);
  for (const entry of normaliseGrants(grants).sections) {
    if (!entry.view) continue;
    const verbs = entry.act && actDelegable.includes(entry.section) ? 'view and act' : 'view';
    phrases.push(`${ADMIN_SECTION_LABELS[entry.section]}: ${verbs} (${sectionReachLabel(entry.section)})`);
  }
  for (const name of grants.globals) {
    if (isOneOf(GLOBAL_CAPABILITIES, name)) phrases.push(GLOBAL_CAPABILITY_LABELS[name]);
  }
  return phrases;
}

/** The sections the detail's effective-access preview reports as act-delegable — the same word the list response carries. */
export function actDelegableSectionsOf(preview: Pick<EffectiveAccessPreview, 'sections'>): AdminSection[] {
  return preview.sections.filter((section) => section.actDelegable).map((section) => section.section);
}

/**
 * A capability's line in the grants editor: what it does, what else a member needs for it to work
 * (said before the save, not only in the warning after it), and whether this installation has it on.
 */
export function capabilityDescription(name: GlobalCapability, floorMet: boolean): string {
  const parts = [GLOBAL_CAPABILITY_DESCRIPTIONS[name]];
  const requires = CAPABILITY_REQUIRES[name] ?? [];
  if (requires.length > 0) {
    parts.push(`Members also need ${formatList(requires.map((required) => GLOBAL_CAPABILITY_LABELS[required]))}, from this group or another.`);
  }
  if (!floorMet) parts.push('Not configured on this installation — granting it has no effect until an operator switches it on.');
  return parts.join(' ');
}

/** How many phrases a group confers — the same count whichever verb a section's phrase carries, so no server word is needed. */
export function confersCount(grants: GroupGrants): number {
  return describeConfers(grants, []).length;
}

/** The heading each group warning's banner carries; the message says the rest. */
export const GROUP_WARNING_TITLES: Readonly<Record<GroupWarning['code'], string>> = {
  no_scope_access: 'Grants nothing usable',
  global_floor_unmet: 'Inert grant',
  global_dependency_unmet: 'Needs another capability',
};

export type GroupSortKey = 'name' | 'members' | 'confers' | 'warnings' | 'updated';

export function groupSortValue(row: AdminGroupRow, key: GroupSortKey): SortValue {
  switch (key) {
    case 'name':
      return row.name;
    case 'members':
      return row.memberCount;
    case 'confers':
      return confersCount(row.confers);
    case 'warnings':
      return row.warnings.length;
    case 'updated':
      return row.updatedAt;
    default:
      return null;
  }
}

// --- Blast radius (06-modules §2.2; 05-administration §4) ---------------------------------------

export interface BlastRadiusText {
  /** "3 members lose:" — or that nobody is affected. */
  summary: string;
  /** What each of them loses, one phrase per grant. */
  loses: string[];
  /** How many of them lose all access to Calis, stated only when more than none. */
  allAccess: string | null;
  /** Member names to list, with a "+n more" tail beyond `limit`. */
  names: string[];
  more: number;
}

export function describeBlastRadius(radius: GroupBlastRadius, actDelegable: readonly AdminSection[], limit = 8): BlastRadiusText {
  const count = radius.memberCount;
  const loses = describeConfers(radius.loses, actDelegable);
  const summary = count === 0
    ? 'No accounts are in this group, so nobody loses access.'
    : pluralise(count, { one: '# member loses:', other: '# members lose:' });
  const allAccess = radius.membersLosingAllAccess > 0
    ? pluralise(radius.membersLosingAllAccess, {
      one: '# of them loses all access to Calis — this group is their only source of it.',
      other: '# of them lose all access to Calis — this group is their only source of it.',
    })
    : null;
  const names = radius.members.slice(0, limit).map((member) => member.displayName);
  return { summary, loses: count === 0 ? [] : loses.length > 0 ? loses : ['nothing — the group grants nothing'], allAccess, names, more: Math.max(0, radius.members.length - names.length) };
}

// --- Audit log (06-modules §3) ------------------------------------------------------------------

export interface AuditFilters {
  actor: string;
  target: string;
  targetType: string;
  operation: string;
  surface: string;
  outcome: string;
  from: string;
  to: string;
}

export const EMPTY_AUDIT_FILTERS: Readonly<AuditFilters> = Object.freeze({ actor: '', target: '', targetType: '', operation: '', surface: '', outcome: '', from: '', to: '' });

const AUDIT_PARAM_KEYS = ['actor', 'target', 'targetType', 'operation', 'surface', 'outcome', 'from', 'to'] as const;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/u;

/** Reads the filters from the URL. Unknown surface/outcome values and malformed dates degrade to "no filter". */
export function parseAuditFilters(params: URLSearchParams): { filters: AuditFilters; dropped: string[] } {
  const dropped: string[] = [];
  const read = (key: (typeof AUDIT_PARAM_KEYS)[number]): string => (params.get(key) ?? '').trim();
  const surface = read('surface');
  const outcome = read('outcome');
  const from = read('from');
  const to = read('to');
  if (surface !== '' && !isOneOf(AUDIT_SURFACES, surface)) dropped.push('surface');
  if (outcome !== '' && !isOneOf(AUDIT_OUTCOMES, outcome)) dropped.push('outcome');
  if (from !== '' && !DATE_ONLY.test(from)) dropped.push('from');
  if (to !== '' && !DATE_ONLY.test(to)) dropped.push('to');
  return {
    filters: {
      actor: read('actor'),
      target: read('target'),
      targetType: read('targetType'),
      operation: read('operation'),
      surface: dropped.includes('surface') ? '' : surface,
      outcome: dropped.includes('outcome') ? '' : outcome,
      from: dropped.includes('from') ? '' : from,
      to: dropped.includes('to') ? '' : to,
    },
    dropped,
  };
}

export function writeAuditFilters(params: URLSearchParams, filters: AuditFilters): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of AUDIT_PARAM_KEYS) {
    const value = filters[key].trim();
    if (value === '') next.delete(key);
    else next.set(key, value);
  }
  return next;
}

export function activeAuditFilterCount(filters: AuditFilters): number {
  return AUDIT_PARAM_KEYS.filter((key) => filters[key].trim() !== '').length;
}

/** The query the API takes for these filters; empty values are omitted and the sort travels beside them. */
export function auditQuery(filters: AuditFilters, sort: { key: string; dir: 'asc' | 'desc' }, page: { limit: number; offset: number }): Record<string, string | number | undefined> {
  return {
    actor: filters.actor || undefined,
    target: filters.target || undefined,
    targetType: filters.targetType || undefined,
    operation: filters.operation || undefined,
    surface: filters.surface || undefined,
    outcome: filters.outcome || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
    sort: sort.key,
    dir: sort.dir,
    limit: page.limit,
    offset: page.offset,
  };
}

/** Human phrases for the dotted operation names the server records; unknown ones are spelled out. */
const OPERATION_LABELS: Readonly<Record<string, string>> = {
  'session.sign_in': 'Signed in',
  'subject.approve': 'Approved account',
  'subject.reinstate': 'Reinstated account',
  'subject.deny': 'Denied account',
  'subject.suspend': 'Suspended account',
  'subject.sessions.revoke': 'Signed out everywhere',
  'subject.groups.update': 'Changed account groups',
  'subject.delete': 'Deleted account',
  // The env-derived superuser's grant and revocation (auth/superuser.ts; their durable twin, superuser_audit, reads
  // them on the Groups page as Granted and Revoked).
  'subject.superuser.grant': 'Granted superuser',
  'subject.superuser.revoke': 'Revoked superuser',
  'group.create': 'Created group',
  'group.update': 'Changed group details',
  'group.delete': 'Deleted group',
  'group.grants.update': 'Changed group grants',
  'group.members.update': 'Changed group members',
  'fellow.connect': 'Connected Fellow',
  'fellow.disconnect': 'Disconnected Fellow',
  'fellow.sync': 'Synced Fellow meetings',
  'fellow.sync.request': 'Requested a Fellow sync',
  'slack.connect': 'Connected Slack',
  'slack.disconnect': 'Disconnected Slack',
  'slack.connection.revoked': 'Slack access ended',
  'slack.settings.update': 'Changed Slack settings',
  // Written before migration 0007, when a person chose the channels Calis read; kept so old rows read.
  'slack.sources.update': 'Changed Slack channels',
  'slack.exclusions.update': 'Changed the Slack channels Calis won’t read',
  'slack.sync': 'Refreshed the Slack channel list',
  'slack.sync.request': 'Requested a Slack channel refresh',
  // The daily Slack sweep (owner request of 2026-09-30): its row is the day's measurement while it runs dark.
  'slack.sweep': 'Read the day in Slack',
  'meeting.import': 'Imported meeting',
  'meeting.update': 'Updated meeting',
  'meeting.summarise': 'Summarised meeting',
  'meeting.summarise.request': 'Requested a summary',
  'meeting.process.request': 'Processed meeting',
  'meeting.slack_context': 'Checked meeting against Slack',
  'meeting.slack_context.request': 'Requested a Slack check',
  'slack_finding.dismiss': 'Dismissed a Slack finding',
  'slack_finding.restore': 'Restored a Slack finding',
  'assistant.brief': 'Wrote the assistant brief',
  'assistant.brief.request': 'Requested an assistant brief',
  'assistant.refresh_meetings': 'Refreshed recent meetings',
  'todo.import': 'Imported to-do',
  'todo.create': 'Created to-do',
  'todo.update': 'Updated to-do',
  'todo.dismiss_by_check': 'Dismissed to-do after a Slack check',
  'todo.dismiss_by_sweep': 'Dismissed to-do after the daily Slack sweep',
  'todo.dismiss_by_brief': 'Dismissed to-do after an assistant brief',
  // The daily Slack sweep's "Mark done" offer (owner ruling Q8(b)): the person's own click, either way.
  'todo.done_offer_accept': 'Marked done from a Slack suggestion',
  'todo.done_offer_dismiss': 'Turned down a Slack suggestion',
  'todo.delete': 'Deleted to-do',
  // Linked to-dos (ADR 0014): a colleague's tick or un-tick reaching this to-do, a link formed, and one left.
  'todo.done_by_link': 'Ticked off through a linked copy',
  'todo.reopen_by_link': 'Reopened through a linked copy',
  'todo.link': 'Linked to-do',
  'todo.unlink': 'Unlinked to-do',
  // The AI's look at a shared meeting (ruling Q2(a)): only its refusal or failure is audited, by the worker.
  'todo.link_scan': 'Compared colleagues\' to-dos for links',
  // Ticking off in Fellow (owner, 2026-09-30): one row per item written (or not), and the run's refusal or failure.
  'todo.fellow_write': 'Ticked off in Fellow',
  'fellow.items': 'Ticked off in Fellow (run)',
  // Changes made in Fellow (Stage 3): a tick or reopen made there, applied to the to-do or offered to follow here.
  'todo.fellow_report': 'Changed in Fellow',
  // The person's "Try again", or the one click a look offered, from the to-do's row.
  'todo.fellow_request': 'Asked to tick off in Fellow',
  // Import or tie (Stage 2): a Fellow item tied to the person's own Calis to-do instead of imported as a second one.
  'todo.fellow_tie': 'Tied to a Fellow item',
  // The fold (O8(c)): an untouched Fellow to-do dismissed because its item is now tied to the person's Calis AI to-do of
  // the same work (refused, with the reason, when someone had acted on it).
  'todo.dismiss_by_fellow_match': 'Dismissed a Fellow to-do Calis matched',
  // The fold undone by Calis itself: the AI to-do the Fellow to-do gave way to went (a new summary replaced it) and nothing
  // matched the item again, so its Fellow to-do comes back.
  'todo.restore_by_fellow_match': 'Restored a Fellow to-do Calis had matched',
  // "Not the same…" (design §5.5): the person split a Fellow item from the to-do Calis had tied it to.
  'todo.fellow_untie': 'Separated from a Fellow item',
  // Calis's look at a person's Fellow items (Stage 2, O2(b)): only its refusal or failure is audited, by the worker; its
  // ties and imports have their own rows.
  'todo.fellow_match': 'Matched Fellow\'s items to to-dos',
  // The sorting pass over a person's open to-dos in no project (owner ruling 11 of 2026-10-04; ADR 0018): the system's
  // delivery, on the person's behalf, and the person's own "Sort my to-dos now". Neither names a project or a to-do.
  'todo.project_sort': 'Sorted to-dos into projects',
  'todo.project_sort.request': 'Asked Calis to sort their to-dos',
  // Projects (owner rulings of 2026-10-02; ADR 0017). Why no label names a project: the row's label is the project's
  // name, which a delegate reads only as the neutral "Project 1a2b3c4d" — so the phrase says what was done, never to what.
  'project.create': 'Created project',
  'project.rename': 'Renamed project',
  'project.merge': 'Merged projects',
  'project.unmerge': 'Undid a project merge',
  'project.delete': 'Deleted project',
  'project.forget_alias': 'Forgot a project\'s former name',
  // Sharing (rulings S1–S7): the sharer's acts and the recipient's, on the share row.
  'project.share': 'Shared project',
  'project.share_revoke': 'Stopped sharing project',
  'project.share_merge_in': 'Merged in a shared project',
  'project.share_unmerge_in': 'Undid merging in a shared project',
  'project.share_leave': 'Left a shared project',
  'project.share_merge_back': 'Merged back a shared project',
  'project.share_unmerge_back': 'Undid merging back a shared project',
  // The landing backfill (owner ruling 7, "Re-summarise recent meetings now"): the scheduler, as the system, queued one
  // person's recent summarised meetings for a refresh so Calis could sort them into projects. The row's target is the
  // refresh (`assistant_refresh`), so the phrase echoes the person's own "Refreshed recent meetings".
  'project.backfill': 'Refreshed recent meetings for projects',
  // A person's filing of a meeting, a Slack finding and a Slack matter (design §6.3).
  'meeting.projects': 'Changed meeting projects',
  'slack_finding.projects': 'Changed Slack finding projects',
  'assistant.matter_projects': 'Changed Slack matter projects',
  // Executive intelligence (ADR 0019; design §2.12): the report the system or an executive's "Refresh now" wrote, the
  // press itself, the one-off re-summary (its target the refresh, as `project.backfill`'s), each executive's first open of
  // a report and every download, the shared list's changes and each executive's own word on a matter — and, by the owner's
  // ruling of 2026-10-08 on ADR 0019's Open 30 ("Audit each read": "name the operations in the audit vocabulary as the
  // design's §2.12 form does; the audit-log reader shows them"), each executive's day's first read of the pulse, the report
  // list, coverage and the shared list — and, by his ruling of 2026-10-08, "Day counts from first due report", the
  // scheduler's one row recording the first time the first report fell due and waited for the re-summary. No phrase names a
  // report's theme, a matter or a project.
  'executive.report': 'Wrote the executive report',
  'executive.report.wait': 'Held the first executive report for the re-summary',
  'executive.report.request': 'Requested an executive report',
  // The operator's `executive:rereport` (the owner's ruling of 2026-10-16 on the stray instance's reports, "Remove and
  // re-report"): one stored report taken back, with what it read, and its window queued to be written again.
  'executive.report.withdraw': 'Withdrew an executive report to write it again',
  'executive.report.view': 'Opened an executive report',
  'executive.report.export': 'Downloaded an executive report\'s themes',
  'executive.reports.view': 'Viewed the executive reports',
  'executive.pulse.view': 'Viewed the executive pulse',
  'executive.coverage.view': 'Viewed the executive report\'s coverage',
  'executive.backfill': 'Refreshed recent meetings for the executive report',
  'executive.matter.create': 'Added an executive matter',
  'executive.matter.update': 'Changed an executive matter',
  'executive.matter.delete': 'Deleted an executive matter',
  'executive.matter.done': 'Marked an executive matter done',
  'executive.matter.reopen': 'Reopened an executive matter',
  'executive.matter.dismiss': 'Dismissed an executive matter',
  'executive.matter.restore': 'Restored an executive matter',
  'executive.matter.file': 'Filed an executive matter under a project',
  'executive.matter.unfile': 'Took an executive matter out of a project',
  'executive.matter.export': 'Downloaded the executive matters',
  'executive.matters.view': 'Viewed the executive matters',
  // Written once, by migration 0006 (the one-time wipe of production's test data, 2026-09-26).
  'maintenance.test_data_wipe': 'Wiped test meetings, summaries and to-dos',
};

/** Every operation with its own phrase (the rest are spelt out from the key): exported so a test can hold a list to it. */
export function hasOperationLabel(operation: string): boolean {
  return Object.hasOwn(OPERATION_LABELS, operation);
}

/**
 * The phrases of the rows an operation's own phrase would misstate, by the one closed reason word their `after` carries.
 * Why (the review of the Phase 2c landing, 2026-10-10; 06-modules §3.1, the log answers what happened and who did it):
 * since the owner's ruling PM-9 of 2026-10-10 ("A row per ended share") a deletion writes one row per share and link
 * membership it ends, under the operation of the ending — and the administrator who deleted an account was listed as
 * having "Left a shared project", which they never did; the sharer who deleted a project, as having "Stopped sharing
 * project". Likewise a recipient's merge or deletion of the project holding a shared one — and, since the owner's ruling of
 * 2026-10-11 ("Write a row"), their Undo of such a merge — writes `share_unmerge_in`, which read "Undid merging in a shared
 * project" of acts that undid nothing. The project feed says these from the same reasons
 * (`describeProjectActivity`); the log says them in its own grain. A reason with no phrase here keeps the operation's.
 */
const REASONED_OPERATION_LABELS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  'project.share_revoke': { deleted: 'Ended a project share by a deletion', merged: 'Closed a project share by merging projects' },
  'project.share_leave': { deleted: 'Ended a project share by deleting its recipient’s account' },
  // `unmerged`: the owner's ruling of 2026-10-11, "Write a row" — the Undo of such a merge withdraws a merge-back too.
  'project.share_unmerge_in': {
    merged: 'Withdrew a shared project’s merge-back by merging projects',
    unmerged: 'Withdrew a shared project’s merge-back by undoing a merge',
    deleted: 'Unmerged a shared project by deleting a project',
  },
  'todo.unlink': { deleted: 'Unlinked a to-do by a deletion' },
};

/**
 * An operation in the reader's words (05-content §3). With the row's `after`, a row whose one closed reason word changes
 * what was done reads as that (`REASONED_OPERATION_LABELS`); without it — a filter's option, a chip — the operation's own.
 */
export function operationLabel(operation: string, after?: unknown): string {
  if (after !== null && typeof after === 'object' && !Array.isArray(after)) {
    const reason = (after as Record<string, unknown>).reason;
    const phrases = Object.hasOwn(REASONED_OPERATION_LABELS, operation) ? REASONED_OPERATION_LABELS[operation] : undefined;
    if (phrases && typeof reason === 'string' && Object.hasOwn(phrases, reason)) return phrases[reason]!;
  }
  const known = OPERATION_LABELS[operation];
  if (known) return known;
  const words = operation.replace(/[._]+/gu, ' ').trim();
  return words === '' ? operation : words.charAt(0).toUpperCase() + words.slice(1);
}

// Why neutral for Skipped (migration 0050): nothing went wrong and nothing was done — neither a success nor a warning.
export const OUTCOME_TONES: Readonly<Record<AuditOutcome, Tone>> = { success: 'success', refused: 'warning', failed: 'danger', skipped: 'neutral' };

/** "Name <email>" as the audit trail stores actors, split for display; system actors have no address. */
export function splitActorLabel(label: string): { name: string; email: string | null } {
  const match = /^(.*?)\s*<([^>]+)>\s*$/u.exec(label);
  if (!match) return { name: label.trim(), email: null };
  return { name: (match[1] ?? '').trim() || (match[2] ?? '').trim(), email: (match[2] ?? '').trim() || null };
}

export type TargetType = 'subject' | 'meeting' | 'group';

export interface AuditLinkPermissions {
  usersView: boolean;
  groups: boolean;
  /**
   * The reader's own subject id. A meeting is visible to its owner alone (the data-layer ownership
   * filter has no superuser bypass), and a meeting-typed audit row is only ever written by the owner or
   * by the worker acting as them — so the row's actor is the owner, and the link is offered only when
   * that is the reader. Absent: no meeting links.
   */
  viewerId?: string | null;
}

/**
 * Where a record's subject lives (06-modules §3.2: each record links to the entity it names).
 * Only routes the reader can open are offered: a delegate without the Groups section gets the
 * label alone rather than a link to a page that will refuse them, and a meeting is linked only for
 * its owner — for anyone else the page could only apologise (05-client §5.5).
 */
export function auditTargetPath(
  target: AuditEventDto['target'],
  can: AuditLinkPermissions,
  actor?: Pick<AuditEventDto['actor'], 'id'> | null,
): string | null {
  if (!target.id) return null;
  const id = encodeURIComponent(target.id);
  switch (target.type) {
    // Why no link for a deleted account's pseudonym (06-modules §3.2: a record "arrives at that entity, identified"; owner
    // ruling O4): the account is gone, so its page answers 404 — the pseudonym, the very form the server wrote at deletion
    // for this id, says what it is instead.
    case 'subject':
      return can.usersView && target.label !== deletedAccountLabel(target.id) ? `/admin/users/${id}` : null;
    case 'meeting':
      return can.viewerId && actor?.id === can.viewerId ? `/meetings/${id}` : null;
    // A project (ADR 0017) is the actor's own, like a meeting: its record page opens for them alone. A share row
    // (`project_share`) targets the share, which has no page, and may be written by the other party — no link.
    case 'project':
      return can.viewerId && actor?.id === can.viewerId ? `/projects/${id}` : null;
    // A brief, and a refresh of recent meetings, are the owner's alone: their page is the Assistant's.
    case 'assistant_brief':
    case 'assistant_refresh':
      return can.viewerId && actor?.id === can.viewerId ? '/assistant' : null;
    case 'group':
      return can.groups ? `/admin/groups/${id}` : null;
    default:
      return null;
  }
}

export const TARGET_TYPE_LABELS: Readonly<Record<string, string>> = {
  subject: 'Account',
  group: 'Group',
  meeting: 'Meeting',
  todo: 'To-do',
  fellow_connection: 'Fellow connection',
  slack_connection: 'Slack connection',
  assistant_brief: 'Assistant brief',
  assistant_refresh: 'Assistant refresh',
  // Projects (ADR 0017): the same neutral words the delegate view uses (audit.ts DELEGATE_VIEWS).
  project: 'Project',
  project_share: 'Project share',
  // A row about a background job with no record of its own to name (worker.ts auditTarget): the delegate view's own words
  // (audit.ts DELEGATE_VIEWS) — the AI's look at a shared meeting and the sorting pass's rows.
  job: 'Background job',
  // Executive intelligence (ADR 0019): the delegate view's own words (audit.ts DELEGATE_VIEWS).
  executive_report: 'Executive report',
  executive_matter: 'Executive matter',
};

export function targetTypeLabel(type: string): string {
  return TARGET_TYPE_LABELS[type] ?? operationLabel(type);
}

export interface AuditFacets {
  actors: FacetOption[];
  targets: FacetOption[];
  targetTypes: FacetOption[];
  operations: FacetOption[];
  surfaces: FacetOption[];
  outcomes: FacetOption[];
}

/**
 * Facet values from the loaded records plus whatever the URL already selects (kept, labelled by id,
 * so it stays visible and removable). Surface and outcome are closed enums common to every reader,
 * so they are offered in full (04-data §1.13 obligation 4).
 */
export function auditFacets(items: readonly AuditEventDto[], selected: AuditFilters = EMPTY_AUDIT_FILTERS): AuditFacets {
  const actors = new Map<string, string>();
  const targets = new Map<string, string>();
  const targetTypes = new Set<string>();
  const operations = new Set<string>();
  for (const item of items) {
    if (item.actor.id) actors.set(item.actor.id, splitActorLabel(item.actor.label).name);
    if (item.target.id) targets.set(item.target.id, item.target.label ?? `${targetTypeLabel(item.target.type)} ${item.target.id}`);
    targetTypes.add(item.target.type);
    operations.add(item.operation);
  }
  const byLabel = (a: FacetOption, b: FacetOption) => a.label.localeCompare(b.label, 'en', { sensitivity: 'base' });
  const actorOptions = [...actors].map(([value, label]) => ({ value, label })).sort(byLabel);
  const targetOptions = [...targets].map(([value, label]) => ({ value, label })).sort(byLabel);
  const typeOptions = [...targetTypes].map((value) => ({ value, label: targetTypeLabel(value) })).sort(byLabel);
  const operationOptions = [...operations].map((value) => ({ value, label: `${operationLabel(value)} (${value})` })).sort(byLabel);
  if (selected.actor !== '' && !actors.has(selected.actor)) actorOptions.push({ value: selected.actor, label: `Actor ${selected.actor} (none loaded)` });
  if (selected.target !== '' && !targets.has(selected.target)) targetOptions.push({ value: selected.target, label: `Subject ${selected.target} (none loaded)` });
  if (selected.targetType !== '' && !targetTypes.has(selected.targetType)) typeOptions.push({ value: selected.targetType, label: `${targetTypeLabel(selected.targetType)} (none loaded)` });
  if (selected.operation !== '' && !operations.has(selected.operation)) operationOptions.push({ value: selected.operation, label: `${operationLabel(selected.operation)} (${selected.operation})` });
  return {
    actors: actorOptions,
    targets: targetOptions,
    targetTypes: typeOptions,
    operations: operationOptions,
    surfaces: AUDIT_SURFACES.map((value) => ({ value, label: AUDIT_SURFACE_LABELS[value] })),
    outcomes: AUDIT_OUTCOMES.map((value) => ({ value, label: OUTCOME_WORDS[value] })),
  };
}

/** The shared outcome words (enums.ts AUDIT_OUTCOME_LABELS, per 05-content §6). */
export const OUTCOME_WORDS: Readonly<Record<AuditOutcome, string>> = AUDIT_OUTCOME_LABELS;

export function outcomeWord(outcome: AuditOutcome): string {
  return OUTCOME_WORDS[outcome];
}

/**
 * Why a record's act did not go ahead, in words, for beside its Outcome (05-content §6: "A skipped run SHOULD carry its
 * reason beside the word — the word says nothing was done, the reason says why"): the reason code the record's `after`
 * carries — `code`, as the worker writes a job's and a Run now's already-running request writes its own, or `errorCode`
 * or `reason` — worded as the record pages and Admin → Queue word it (`reasonInWords`, pages/lib/activity.ts: one code is
 * never worded two ways, and one with no words of its own is spelt out from its key, never shown raw), starting with a
 * capital. Null for a success, and for a record that carries no reason code.
 */
export function outcomeReason(record: Pick<AuditEventDto, 'outcome' | 'after'>): string | null {
  if (record.outcome === 'success') return null;
  const after = record.after;
  if (typeof after !== 'object' || after === null || Array.isArray(after)) return null;
  const fields = after as Record<string, unknown>;
  const code = [fields.code, fields.errorCode, fields.reason].find((value): value is string => typeof value === 'string' && value.trim() !== '');
  if (code === undefined) return null;
  const words = reasonInWords(code);
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function describeAuditFilters(filters: AuditFilters, facets: AuditFacets): FilterChip[] {
  const chips: FilterChip[] = [];
  const labelOf = (options: FacetOption[], value: string): string => options.find((option) => option.value === value)?.label ?? value;
  if (filters.actor !== '') chips.push({ key: 'actor', label: `Actor: ${labelOf(facets.actors, filters.actor)}` });
  if (filters.target !== '') chips.push({ key: 'target', label: `Subject: ${labelOf(facets.targets, filters.target)}` });
  if (filters.targetType !== '') chips.push({ key: 'targetType', label: `Kind: ${labelOf(facets.targetTypes, filters.targetType)}` });
  if (filters.operation !== '') chips.push({ key: 'operation', label: `Operation: ${operationLabel(filters.operation)}` });
  if (filters.surface !== '') chips.push({ key: 'surface', label: `Surface: ${labelOf(facets.surfaces, filters.surface)}` });
  if (filters.outcome !== '') chips.push({ key: 'outcome', label: `Outcome: ${labelOf(facets.outcomes, filters.outcome)}` });
  if (filters.from !== '' && filters.to !== '') chips.push({ key: 'period', label: `Period: ${filters.from} to ${filters.to}` });
  else if (filters.from !== '') chips.push({ key: 'from', label: `From: ${filters.from}` });
  else if (filters.to !== '') chips.push({ key: 'to', label: `Until: ${filters.to}` });
  return chips;
}

/** The value the highlight param takes when a page links to "the newest record about this entity". */
export const HIGHLIGHT_LATEST = 'latest';

/**
 * Resolves `?highlight=` against the loaded page: an id is returned as is, `latest` becomes the id
 * of the newest loaded record. The table then reports whether the id is actually present.
 */
export function resolveHighlight(param: string | null, items: readonly AuditEventDto[]): string | null {
  if (!param || param.trim() === '') return null;
  if (param !== HIGHLIGHT_LATEST) return param;
  let newest: AuditEventDto | null = null;
  for (const item of items) {
    if (!newest || item.createdAt > newest.createdAt) newest = item;
  }
  return newest?.id ?? null;
}

// --- Bulk approval (06-modules §1.3 R1) ---------------------------------------------------------

export interface BulkStep {
  id: string;
  name: string;
  ok: boolean;
  message?: string;
}

/** One sentence summarising a sequential bulk approval, naming the failures. */
export function summariseBulk(steps: readonly BulkStep[]): { tone: 'success' | 'warning' | 'error'; message: string } {
  const failed = steps.filter((step) => !step.ok);
  const succeeded = steps.length - failed.length;
  if (failed.length === 0) return { tone: 'success', message: pluralise(steps.length, { one: 'Approved # account.', other: 'Approved # accounts.' }) };
  const names = formatList(failed.map((step) => step.name));
  if (succeeded === 0) return { tone: 'error', message: `No accounts were approved. Failed: ${names}.` };
  return { tone: 'warning', message: `Approved ${succeeded} of ${steps.length}. Not approved: ${names}.` };
}

/** Approval needs at least one chosen group that gives access to Calis (permission-model 02 §2.2). */
export function approvalBlocker(chosen: readonly string[], groups: readonly AdminGroupRow[]): string | null {
  if (chosen.length === 0) return 'Choose at least one group to enable approval.';
  const confersAccess = chosen.some((id) => groups.find((group) => group.id === id)?.confers.scopeMember === true);
  if (!confersAccess) return 'Choose at least one group that gives access to Calis — the chosen groups grant nothing usable on their own.';
  return null;
}
